import {
  Box,
  Button,
  Flex,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import moment from "moment";
import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiCalendar, FiLock } from "react-icons/fi";
import type { SourcingEventV2Dto, SourcingEventV2PatchDto } from "../../../../../autogen/bff-api";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";
import { ThemedDateTimePicker } from "../../../../../common/input/DateTimeSelectors/ThemedDateTimePicker";
import { MILESTONE_NODES, type MilestoneKey, type MilestoneNode } from "../utils/milestones";
import { autoKarensDays, minRequestDays, minTenderDays } from "../utils/karens";
import { isDpsCompetition, isDpsScheme, isQualificationScheme, isTwoStageProcedure } from "../utils/procedure";
import { daysBetween, milestoneOffsetPatch, tenderAnchor } from "../utils/milestoneOffsets";

interface MilestoneTimelineProps {
  event: SourcingEventV2Dto;
  mode: "edit" | "summary";
  deadlineBadge?: ReactNode;
}

type OffsetPlanField =
  | "questionDaysBeforeBid"
  | "evaluationOffsetDays"
  | "awardOffsetDays"
  | "contractSigningOffsetDays";

const PLAN_FIELD: Partial<Record<MilestoneKey, OffsetPlanField>> = {
  questionDeadline: "questionDaysBeforeBid",
  evaluationStart: "evaluationOffsetDays",
  awardDecision: "awardOffsetDays",
  contractSigning: "contractSigningOffsetDays",
};

// The two-stage stage-1 deadline (eForms BT-1311).
const REQUEST_DEADLINE_NODE = {
  key: "requestDeadline",
  labelKey: "Deadline for requests to participate",
  editable: true,
} as const;

// An ordning's own dates (FOA § 26-6). They are announced once and edited in the DPS card above,
// so the plan shows them without a picker of its own.
// t('The system opens') t('The system expires')
const SCHEME_VALIDITY_NODES = [
  { key: "dpsValidFrom", labelKey: "The system opens", editable: false },
  { key: "dpsValidTo", labelKey: "The system expires", editable: false },
] as const;

type TimelineNode =
  | MilestoneNode
  | typeof REQUEST_DEADLINE_NODE
  | (typeof SCHEME_VALIDITY_NODES)[number];

const isMilestoneNode = (node: TimelineNode): node is MilestoneNode => "dateField" in node;

// Derive the displayed offset from milestoneDates — this is the sync guarantee.
const deriveOffset = (
  nodeKey: MilestoneKey,
  dates: SourcingEventV2Dto["milestoneDates"],
  // Two-stage measures the tender period from the forespørselsfrist, not the announcement.
  twoStageRequestDeadline: string | null = null,
): number | null => {
  const tender = tenderAnchor(dates);
  switch (nodeKey) {
    case "bidDeadline": {
      const anchor = twoStageRequestDeadline ?? dates.announcement;
      return anchor && tender ? daysBetween(anchor, tender) : null;
    }
    case "questionDeadline":
      return dates.questionDeadline && tender ? daysBetween(dates.questionDeadline, tender) : null;
    case "evaluationStart":
      return tender && dates.evaluationTarget ? daysBetween(tender, dates.evaluationTarget) : null;
    case "awardDecision":
      return dates.evaluationTarget && dates.awardTarget
        ? daysBetween(dates.evaluationTarget, dates.awardTarget)
        : null;
    case "contractSigning": {
      const anchor = dates.karensDate ?? dates.awardTarget;
      return anchor && dates.contractSigningTarget
        ? daysBetween(anchor, dates.contractSigningTarget)
        : null;
    }
    default:
      return null;
  }
};

// t() dynamic keys — langen extraction:
// t('days before bid deadline') t('days after announcement') t('days after bid deadline')
// t('days after evaluation') t('days after award') t('days after standstill')
// t('days after request deadline') t('Planned bid deadline') t('The competition opens')
// t('Day 0 · re-anchors at publish') t('Set exact date')
// t('Change') t('Announcement date')
const RELATIVE_LABEL: Partial<Record<MilestoneKey, string>> = {
  questionDeadline: "days before bid deadline",
  bidDeadline: "days after announcement",
  evaluationStart: "days after bid deadline",
  awardDecision: "days after evaluation",
  contractSigning: "days after standstill",
};

const END_OF_DAY = { hours: 23, minutes: 59 };

const formatDateLine1 = (iso: string): string =>
  new Date(iso).toLocaleDateString("nb-NO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Oslo",
  });

const formatTimeLine2 = (iso: string): string =>
  new Date(iso).toLocaleTimeString("nb-NO", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Oslo",
  });

const NodeDot = ({
  index,
  active,
  locked,
}: {
  index: number;
  active: boolean;
  locked?: boolean;
}) => (
  <Flex
    align="center"
    justify="center"
    boxSize="6"
    flexShrink={0}
    borderRadius="full"
    fontSize="xs"
    fontWeight="bold"
    bg={active ? "smPrimary" : "white"}
    color={active ? "white" : "smMuted"}
    borderWidth={locked ? "0" : "2px"}
    borderStyle={locked ? "none" : "solid"}
    borderColor={locked ? undefined : active ? "smPrimary" : "smBorder"}
  >
    {locked ? <FiLock size={11} /> : index + 1}
  </Flex>
);

const StackedDate = ({ iso }: { iso: string | null | undefined }) => {
  const { t } = useTranslation();
  if (!iso) {
    return (
      <Text fontSize="xs" color="smMuted" fontFamily="mono">
        {t("Not set")}
      </Text>
    );
  }
  return (
    <Box fontFamily="mono" lineHeight="1.3">
      <Text fontSize="sm" fontWeight="semibold" color="smText" whiteSpace="nowrap">
        {formatDateLine1(iso)}
      </Text>
      <Text fontSize="xs" color="smMuted" whiteSpace="nowrap">
        {formatTimeLine2(iso)}
      </Text>
    </Box>
  );
};

const STEPPER_DEBOUNCE_MS = 400;

const addDays = (anchorIso: string, days: number): string =>
  new Date(new Date(anchorIso).getTime() + days * 86_400_000).toISOString();

const OffsetStepper = ({
  nodeKey,
  offset,
  eventId,
  announcementIso,
  twoStage,
}: {
  nodeKey: MilestoneKey;
  offset: number | null;
  eventId: string;
  announcementIso: string | null | undefined;
  twoStage: boolean;
}) => {
  const { t } = useTranslation();
  const { updateField } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const planField = PLAN_FIELD[nodeKey];
  const isBid = nodeKey === "bidDeadline";
  // Two-stage moves the planned tender period; single-stage moves the absolute deadline itself.
  const isPlannedBid = isBid && twoStage;
  const lastWrittenRef = useRef<number | null>(null);
  useEffect(() => {
    if (lastWrittenRef.current === offset) lastWrittenRef.current = null;
  }, [offset]);

  if (!planField && !isBid) return null;
  if (isBid && !isPlannedBid && !announcementIso) return null;

  const display = lastWrittenRef.current ?? offset ?? 0;

  const write = (delta: number) => {
    const fresh = lastWrittenRef.current ?? offset ?? 0;
    const next = Math.max(0, fresh + delta);
    lastWrittenRef.current = next;
    updateField(
      isPlannedBid ? { eventData: { milestonePlan: { tenderPeriodOffsetDays: next } } }
      : isBid ? { eventData: { bidSubmissionDeadline: addDays(announcementIso as string, next) } }
      : { eventData: { milestonePlan: { [planField as OffsetPlanField]: next } } },
      { debounceMs: STEPPER_DEBOUNCE_MS },
    );
  };

  const handleMinus = () => write(-1);
  const handlePlus = () => write(+1);

  return (
    <Flex
      align="center"
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="md"
      overflow="hidden"
      h="8"
      flexShrink={0}
    >
      <Box
        as="button"
        type="button"
        w="7"
        h="full"
        fontSize="md"
        color="smMuted"
        _hover={{ bg: "smPrimarySofter", color: "smPrimary" }}
        onClick={handleMinus}
        aria-label={t("Decrease offset") ?? undefined}
      >
        –
      </Box>
      <Flex
        align="center"
        justify="center"
        minW="8"
        h="full"
        borderLeftWidth="1px"
        borderRightWidth="1px"
        borderColor="smBorder"
        px="1"
        fontFamily="mono"
        fontWeight="semibold"
        fontSize="sm"
      >
        {display}
      </Flex>
      <Box
        as="button"
        type="button"
        w="7"
        h="full"
        fontSize="md"
        color="smMuted"
        _hover={{ bg: "smPrimarySofter", color: "smPrimary" }}
        onClick={handlePlus}
        aria-label={t("Increase offset") ?? undefined}
      >
        +
      </Box>
    </Flex>
  );
};

const CalendarOverride = ({
  nodeKey,
  iso,
  eventId,
  milestoneDates,
  twoStage,
  requestDeadline,
}: {
  nodeKey: MilestoneKey;
  iso: string | null | undefined;
  eventId: string;
  milestoneDates: SourcingEventV2Dto["milestoneDates"];
  twoStage: boolean;
  requestDeadline: string | null;
}) => {
  const { t } = useTranslation();
  const { updateField } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleChange = (pickedIso?: string) => {
    if (!pickedIso) return;
    const patch = milestoneOffsetPatch(nodeKey, pickedIso, milestoneDates, twoStage, requestDeadline);
    if (patch) {
      updateField(patch);
      onClose();
    }
  };

  const toDate = (iso: string | null | undefined) => (iso ? new Date(iso) : undefined);
  // Everything downstream of the tender phase hangs off the planned tilbudsfrist, which equals the
  // real one for single-stage and is projected from the forespørselsfrist until the invitation.
  const minDate =
    nodeKey === "bidDeadline" ? toDate(twoStage ? requestDeadline : milestoneDates.announcement)
    : nodeKey === "evaluationStart" ? toDate(tenderAnchor(milestoneDates))
    : nodeKey === "awardDecision" ? toDate(milestoneDates.evaluationTarget)
    : nodeKey === "contractSigning" ? toDate(milestoneDates.karensDate ?? milestoneDates.awardTarget)
    : undefined;
  // Question deadline is "before bid" — bid is the upper bound.
  const maxDate = nodeKey === "questionDeadline" ? toDate(tenderAnchor(milestoneDates)) : undefined;

  return (
    <Popover isOpen={isOpen} onClose={onClose} isLazy placement="top">
      <PopoverTrigger>
        <IconButton
          aria-label={t("Set exact date") ?? "Set exact date"}
          icon={<FiCalendar size={13} />}
          size="xs"
          variant="ghost"
          color="smPrimary"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="smTealShadow"
          bg="smPrimarySofter"
          _hover={{ bg: "smTealShadow" }}
          onClick={onOpen}
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="auto" bg="transparent" border="none" boxShadow="none" _focus={{ boxShadow: "none" }}>
          <PopoverBody p="0">
            <ThemedDateTimePicker
              ariaLabel={t("Set exact date") ?? "Set exact date"}
              date={iso}
              onChange={handleChange}
              defaultTime={END_OF_DAY}
              minDate={minDate}
              maxDate={maxDate}
              inline
            />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

const ANNOUNCEMENT_FIELD_PATH = "eventData.milestonePlan.announcementDate";

const AnnouncementCalendarOverride = ({
  iso,
  eventId,
}: {
  iso: string | null;
  eventId: string;
}) => {
  const { t } = useTranslation();
  const { updateField, fieldStatus } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(
    eventId,
  );
  const { isOpen, onOpen, onClose } = useDisclosure();

  const status = fieldStatus(ANNOUNCEMENT_FIELD_PATH);
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current) return;
    if (iso !== null) return;
    if (status === "saving") return;
    prefilledRef.current = true;
    updateField({
      eventData: {
        milestonePlan: {
          announcementDate: moment()
            .set({ hour: 23, minute: 59, second: 0, millisecond: 0 })
            .toISOString(),
        },
      },
    });
  }, [iso, status, updateField]);

  const handleChange = (pickedIso?: string) => {
    updateField({ eventData: { milestonePlan: { announcementDate: pickedIso ?? null } } });
    if (pickedIso) onClose();
  };

  return (
    <Popover isOpen={isOpen} onClose={onClose} isLazy placement="top">
      <PopoverTrigger>
        <IconButton
          aria-label={t("Set exact date") ?? "Set exact date"}
          icon={<FiCalendar size={13} />}
          size="xs"
          variant="ghost"
          color="smPrimary"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="smTealShadow"
          bg="smPrimarySofter"
          _hover={{ bg: "smTealShadow" }}
          onClick={onOpen}
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="auto" bg="transparent" border="none" boxShadow="none" _focus={{ boxShadow: "none" }}>
          <PopoverBody p="0">
            <ThemedDateTimePicker
              ariaLabel={t("Set exact date") ?? "Set exact date"}
              date={iso}
              onChange={handleChange}
              defaultTime={END_OF_DAY}
              inline
            />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

const RequestDeadlineCalendarOverride = ({
  iso,
  eventId,
}: {
  iso: string | null;
  eventId: string;
}) => {
  const { t } = useTranslation();
  const { updateField } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleChange = (pickedIso?: string) => {
    if (!pickedIso) return;
    updateField({ eventData: { requestDeadline: pickedIso, requestDeadlineConfirmed: true } });
    onClose();
  };

  return (
    <Popover isOpen={isOpen} onClose={onClose} isLazy placement="top">
      <PopoverTrigger>
        <IconButton
          aria-label={t("Set exact date") ?? "Set exact date"}
          icon={<FiCalendar size={13} />}
          size="xs"
          variant="ghost"
          color="smPrimary"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="smTealShadow"
          bg="smPrimarySofter"
          _hover={{ bg: "smTealShadow" }}
          onClick={onOpen}
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="auto" bg="transparent" border="none" boxShadow="none" _focus={{ boxShadow: "none" }}>
          <PopoverBody p="0">
            <ThemedDateTimePicker
              ariaLabel={t("Set exact date") ?? "Set exact date"}
              date={iso}
              onChange={handleChange}
              defaultTime={END_OF_DAY}
              inline
            />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

const StatutoryBadge = ({ days }: { days: number }) => {
  const { t } = useTranslation();
  return (
    <Flex
      direction="column"
      align="center"
      gap="0"
      mt="1"
      px="3"
      py="1.5"
      borderRadius="md"
      bg="smWarningSoft"
      borderWidth="1px"
      borderColor="smWarningSoftBorder"
      fontSize="xs"
      fontWeight="bold"
      color="smWarning"
      textTransform="uppercase"
      letterSpacing="wider"
      lineHeight="1.3"
    >
      <Text>{t("Statutory")}</Text>
      <Text>{days} {t("days")}</Text>
    </Flex>
  );
};

// The server refuses a tilbudsfrist below the statutory minimum, and that rejection has to land on
// the node the buyer just picked — otherwise the date silently fails to save and the summary
// reports a deadline that was never stored.
const BidDeadlineFieldError = ({ eventId }: { eventId: string }) => {
  const { fieldError } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const formatFieldError = useFieldErrorTranslation();
  const error = fieldError("eventData.bidSubmissionDeadline");
  if (!error) return null;
  return (
    <Text fontSize="xs" color="smError" mt="1">
      {formatFieldError(error)}
    </Text>
  );
};

const BidMinimumWarning = ({ minDays, minimumAt }: { minDays: number; minimumAt: string }) => {
  const { t } = useTranslation();
  return (
    <Text fontSize="xs" color="smError" mt="1">
      {t("zz_FieldErrorCode_TenderDeadlineBelowMinimum", { minDays, minimumAt })}
    </Text>
  );
};

const ConfirmRequestDeadlineButton = ({ eventId, isDisabled }: { eventId: string; isDisabled: boolean }) => {
  const { t } = useTranslation();
  const { updateField } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  return (
    <Button
      size="xs"
      colorScheme="teal"
      variant="solid"
      isDisabled={isDisabled}
      onClick={() => updateField({ eventData: { requestDeadlineConfirmed: true } })}
    >
      {t("Confirm deadline")}
    </Button>
  );
};

const PendingDeadlineConfirmation = ({
  label,
  mode,
  picker,
  note,
  children,
}: {
  label: string;
  mode: "edit" | "summary";
  picker: ReactNode;
  note?: ReactNode;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  return (
    <Flex
      mt="6"
      px="4"
      py="3"
      gap="3"
      borderRadius="md"
      bg="smWarningSoft"
      borderWidth="1px"
      borderColor="smWarningSoftBorder"
      direction={{ base: "column", md: "row" }}
      align={{ base: "flex-start", md: "center" }}
      justify="space-between"
    >
      <Box>
        <Text fontSize="sm" fontWeight="semibold" color="smText">
          {label}
        </Text>
        <Text fontSize="sm" color="smText">
          {t("The deadline is a suggestion from the system. Confirm it, or choose another date.")}
        </Text>
        {note}
      </Box>
      <Flex gap="2" align="center" flexShrink={0}>
        {mode === "summary" && picker}
        {children}
      </Flex>
    </Flex>
  );
};

export const MilestoneTimeline = ({ event, mode, deadlineBadge }: MilestoneTimelineProps) => {
  const { t } = useTranslation();
  const { id: eventId, milestoneDates, eventData } = event;
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  const foaPart = publicContext?.foaPart ?? null;
  const procedureType = publicContext?.procedureType ?? null;
  const regulation = publicContext?.regulation ?? null;
  const karensDays = autoKarensDays(regulation, foaPart, procedureType);
  const minBidDays = minTenderDays(regulation, foaPart, procedureType);
  const twoStage = isTwoStageProcedure(procedureType);
  // The two halves of a DPS plan: the system ends at its validity, a purchase starts at the
  // invitation and holds the tender phase.
  const schemePlan = isDpsScheme(procedureType) || isQualificationScheme(procedureType);
  const competitionPlan = isDpsCompetition(procedureType);
  const scheme = publicContext?.dpsScheme ?? publicContext?.qualificationScheme ?? null;

  // Fall back to the plan announcement when the derived value is null, else bid picks no-op (SM-676).
  const resolvedDates = {
    ...milestoneDates,
    announcement: milestoneDates.announcement ?? eventData.milestonePlan?.announcementDate ?? null,
  };

  const minReqDays = minRequestDays(regulation, foaPart, publicContext?.procedureType);
  const requestDeadline = eventData.requestDeadline ?? null;
  const requestBelowMinimum =
    minReqDays != null &&
    requestDeadline != null &&
    moment(requestDeadline).isBefore(moment().add(minReqDays, "days"));
  const requestDeadlineAwaitsConfirmation =
    twoStage &&
    !isQualificationScheme(procedureType) &&
    requestDeadline != null &&
    eventData.requestDeadlineConfirmed === false;

  const baseNodes = MILESTONE_NODES.filter(
    (node) => node.key !== "standstillExpiry" || karensDays != null,
  );
  const nodes: readonly TimelineNode[] =
    schemePlan ?
      [
        ...baseNodes.filter((node) => node.key === "announcement"),
        // § 13-1 intake has no deadline — the validity is the whole window.
        ...(isQualificationScheme(procedureType) ? [] : [REQUEST_DEADLINE_NODE]),
        ...SCHEME_VALIDITY_NODES,
      ]
    : twoStage ?
      baseNodes.flatMap((node) =>
        node.key === "announcement" ? [node, REQUEST_DEADLINE_NODE] : [node],
      )
    : baseNodes;

  return (
    <Box>
      <Box position="relative">
      {/* xl (1280px) is where all 7 nodes fit horizontally; below it the rail turns vertical. */}
      <Box
        position="absolute"
        bg="smBorder"
        zIndex={0}
        top={{ base: "10px", xl: "11px" }}
        left={{ base: "11px", xl: "3%" }}
        right={{ base: "auto", xl: "3%" }}
        bottom={{ base: "10px", xl: "auto" }}
        height={{ base: "auto", xl: "2px" }}
        width={{ base: "2px", xl: "auto" }}
      />

      <Flex
        direction={{ base: "column", xl: "row" }}
        flexWrap="nowrap"
        justify={{ xl: "center" }}
        position="relative"
        zIndex={1}
        gap={{ base: 3, xl: 1 }}
      >
        {nodes.map((node, index) => {
          const isBid = node.key === "bidDeadline";
          const isAnnouncement = node.key === "announcement";
          const isStandstill = node.key === "standstillExpiry";
          const isRequest = node.key === "requestDeadline";
          const dateIso =
            node.key === "requestDeadline" ? eventData.requestDeadline ?? null
            : node.key === "dpsValidFrom" ? scheme?.validFrom ?? null
            : node.key === "dpsValidTo" ? scheme?.validTo ?? null
            // Two-stage has no real tilbudsfrist before the invitation — show the planned one.
            : isBid ? tenderAnchor(resolvedDates)
            : isMilestoneNode(node) ? resolvedDates[node.dateField]
            : null;
          const offset =
            isMilestoneNode(node) && node.key !== "standstillExpiry"
              ? deriveOffset(node.key, resolvedDates, twoStage ? eventData.requestDeadline ?? null : null)
              : null;
          const relativeLabel =
            !isMilestoneNode(node) ? undefined
            : twoStage && isBid ? "days after request deadline"
            : RELATIVE_LABEL[node.key];
          // The two-stage bid node is the *planned* tilbudsfrist: the real one is set at the § 23-2
          // invitation, but the buyer still plans the tender period from the forespørselsfrist.
          const isEditable = mode === "edit" && node.editable && !isStandstill;

          return (
            <Flex
              key={node.key}
              flex={{ xl: "1 1 0" }}
              maxW={{ xl: "160px" }}
              direction={{ base: "row", xl: "column" }}
              align={{ base: "flex-start", xl: "center" }}
              textAlign={{ base: "left", xl: "center" }}
              gap={{ base: 3, xl: 2 }}
              px={{ xl: 1 }}
            >
              <NodeDot index={index} active={twoStage ? isRequest : isBid} locked={isStandstill} />

              <Flex
                direction="column"
                align={{ base: "flex-start", xl: "center" }}
                gap="1"
                minW="0"
                flex="1"
              >
                {/* Label row */}
                <Box w="full" minH={{ xl: "2.5rem" }}>
                  <Flex
                    align="center"
                    gap="1.5"
                    justify={{ base: "flex-start", xl: "center" }}
                    wrap="wrap"
                  >
                    <Text fontSize="sm" fontWeight="semibold" color="smText" lineHeight="1.2">
                      {t(
                        twoStage && isBid ? "Planned bid deadline"
                        : competitionPlan && isAnnouncement ? "The competition opens"
                        : schemePlan && isRequest ? "Deadline for the first intake"
                        : node.labelKey,
                      )}
                    </Text>
                    {isBid && deadlineBadge && (
                      <Box as="span" display="inline-flex" alignItems="center" lineHeight="1">
                        {deadlineBadge}
                      </Box>
                    )}
                  </Flex>
                </Box>

                {isEditable ? (
                  <Flex direction="column" align={{ base: "flex-start", xl: "center" }} gap="1">
                    {isAnnouncement ? (
                      <AnnouncementCalendarOverride
                        iso={eventData.milestonePlan?.announcementDate ?? null}
                        eventId={eventId}
                      />
                    ) : node.key === "requestDeadline" ? (
                      <RequestDeadlineCalendarOverride iso={requestDeadline} eventId={eventId} />
                    ) : isMilestoneNode(node) ? (
                      <CalendarOverride
                        nodeKey={node.key}
                        iso={dateIso}
                        eventId={eventId}
                        milestoneDates={resolvedDates}
                        twoStage={twoStage}
                        requestDeadline={eventData.requestDeadline ?? null}
                      />
                    ) : null}
                    <StackedDate iso={dateIso} />
                  </Flex>
                ) : (
                  <StackedDate iso={dateIso} />
                )}

                {/* Controls — offset stepper (most nodes) or day-0 helper (announcement) */}
                {isEditable &&
                  (isAnnouncement ? (
                    <Text fontSize="xs" color="smMuted" textAlign={{ base: "left", xl: "center" }}>
                      {t("Day 0 · re-anchors at publish")}
                    </Text>
                  ) : node.key === "requestDeadline" ? (
                    minReqDays != null && (
                      <Text fontSize="xs" color="smMuted" textAlign={{ base: "left", xl: "center" }}>
                        {schemePlan
                          ? t("At least {{minDays}} days. Applications keep arriving after it.", {
                              minDays: minReqDays,
                            })
                          : t("The minimum is {{minDays}} days.", { minDays: minReqDays })}
                      </Text>
                    )
                  ) : isMilestoneNode(node) ? (
                    <Flex
                      direction={{ base: "row", xl: "column" }}
                      align="center"
                      gap="1.5"
                      mt="1"
                    >
                      <OffsetStepper
                        nodeKey={node.key}
                        offset={offset}
                        eventId={eventId}
                        announcementIso={resolvedDates.announcement}
                        twoStage={twoStage}
                      />
                      {relativeLabel && (
                        <Text
                          fontSize="xs"
                          color="smMuted"
                          textAlign={{ base: "left", xl: "center" }}
                        >
                          {t(relativeLabel)}
                        </Text>
                      )}
                    </Flex>
                  ) : null)}

                {isEditable && isBid && <BidDeadlineFieldError eventId={eventId} />}

                {/* Soft bid-minimum warning — bid node only, edit mode, when the derived
                    offset is below the legal minimum tender period. Informational, not a block. */}
                {isEditable &&
                  isBid &&
                  minBidDays != null &&
                  offset != null &&
                  offset < minBidDays &&
                  resolvedDates.announcement != null && (
                    <BidMinimumWarning
                      minDays={minBidDays}
                      minimumAt={new Date(
                        new Date(resolvedDates.announcement).getTime() + minBidDays * 86_400_000,
                      ).toISOString()}
                    />
                  )}

                {/* Mirror of the bid-minimum warning for the stage-1 deadline. The check runs
                    from now, not from the planned announcement. */}
                {isEditable && isRequest && requestBelowMinimum && (
                  <Text fontSize="xs" color="smError" mt="1">
                    {t("zz_FieldErrorCode_RequestDeadlineBelowMinimum")}
                  </Text>
                )}

                {twoStage && isBid && (
                  <Text
                    fontSize="xs"
                    color="smMuted"
                    mt="0.5"
                    textAlign={{ base: "left", xl: "center" }}
                  >
                    {t("Set when the invitation to tender is sent")}
                  </Text>
                )}

                {node.key === "standstillExpiry" && karensDays != null && (
                  <StatutoryBadge days={karensDays} />
                )}

                {/* Announcement day-0 label in summary / non-edit modes */}
                {isAnnouncement && !isEditable && (
                  <Text fontSize="xs" color="smMuted" mt="0.5">
                    {t("Day 0 · re-anchors at publish")}
                  </Text>
                )}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
      </Box>

      {requestDeadlineAwaitsConfirmation && (
        <PendingDeadlineConfirmation
          label={t(schemePlan ? "Deadline for the first intake" : "Deadline for requests to participate")}
          mode={mode}
          picker={<RequestDeadlineCalendarOverride iso={requestDeadline} eventId={eventId} />}
          note={
            requestBelowMinimum && (
              <Text fontSize="xs" color="smError" mt="1">
                {t("zz_FieldErrorCode_RequestDeadlineBelowMinimum")}
              </Text>
            )
          }
        >
          <ConfirmRequestDeadlineButton eventId={eventId} isDisabled={requestBelowMinimum} />
        </PendingDeadlineConfirmation>
      )}

    </Box>
  );
};
